import React, { useMemo, memo } from 'react';
import { uniq } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useAtom } from 'jotai';
import { Button } from 'antd';
import MetricSelectorPopup from '@components/common/MetricSelectorPopup';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { refreshTrends } from '@store/common/actions';
import useSupportedMetrics from '@hooks/metrics/useSupportedMetrics';
import useSupportedFlowMetrics from '@hooks/metrics/useSupportedFlowMetrics';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import { supportedMetricsSelector } from '@store/common/selectors';
import { setDimensionMetrics } from '@store/trends/base/actions';
import { dimensionMetricsSelector, globalOrderBySelector } from '@store/trends/base/selectors';
import { metricsCountLimit } from '@store/trends/base/dimensionSaga';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';
import { getSecondaryMetric } from '@src/utils/metrics/metricHelpers';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { currentKpiIdAtom } from '@src/atoms/panels';

function MetricSelector({ RenderBtn = Button }: any) {
  const dispatch = useDispatch();
  const metrics: Record<string, any> = useSelector(supportedMetricsSelector);
  const selectedMetrics: any[] = useSelector(dimensionMetricsSelector);
  const metric = useSelector(selectedMetricSelector);
  const orderBy = useSelector(globalOrderBySelector);
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);
  const globalExcludeMetricsCategory = useGlobalExcludeMetricCategories([]);
  const limitation = useCurrentLimitation();
  const { flowId } = useDrilldownMeta();

  const supportedMetrics = useSupportedMetrics({
    excludeCategories: globalExcludeMetricsCategory,
    limitation,
    allMetrics: metrics,
  });

  const flowData = useSupportedFlowMetrics({
    allMetrics: metrics,
    flowId,
  });

  const onApply = (metrics, kpiChanged) => {
    let dimensionMetrics = metrics;

    if (CURRENT_PRODUCT_MODE === 'APP') {
      dimensionMetrics = Array.from(
        new Set(
          metrics.map((it) => [it, getSecondaryMetric({ metricKey: it, defaultValue: it })]).flat(),
        ),
      );
    }

    dispatch(setDimensionMetrics(dimensionMetrics));
    // refresh data on page
    if (kpiChanged) {
      refreshTrends();
    }
  };

  // metric filter replaces applied metrics with pre-defined metrics
  // primary metric should be locked only if it's checked
  const lockedMetrics: string[] = useMemo(() => {
    return uniq([metric, orderBy]).filter((m) => selectedMetrics.includes(m));
  }, [metric, orderBy, selectedMetrics]);

  return (
    <MetricSelectorPopup
      supportedMetrics={supportedMetrics}
      flowData={flowData}
      selectedMetrics={selectedMetrics}
      lockedMetrics={lockedMetrics}
      onApply={onApply}
      metricsCountLimit={metricsCountLimit}
      buttonText={'Dimension Metrics'}
      RenderBtn={RenderBtn}
      config={limitation?.metrics?.config}
      currentKpiId={currentKpiId}
      onKpiChange={setCurrentKpiId}
    />
  );
}

export default memo(MetricSelector);
